// Account Settings → Profile Overview (1.27.70). Everything here is read from the
// signed-in member's own payload; editing happens on /settings/profile.
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Avatar } from '@/ui';
import { Icon } from '@/icons';
import { formatCount } from '@/components/formatCount';
import type { User } from '@/core/models';
import { CardHead, DashCard, Pill } from '../../dashboard/DashCard';
import { LinkButton } from '../../dashboard/dashStyles';

const Body = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 24px;
  padding-top: 16px;
  @container (max-width: 1059.98px) {
    grid-template-columns: auto minmax(0, 1fr);
  }
  @container (max-width: 619.98px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 104px;
  height: 104px;
`;

const CameraLink = styled(Link)`
  position: absolute;
  right: -4px;
  bottom: -4px;
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surfaceRaised};
  box-shadow: 0 0 0 3px ${({ theme }) => theme.color.surface};
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`;

const Name = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  line-height: ${({ theme }) => theme.lineHeight.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  overflow-wrap: anywhere;
`;

const Supporter = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.color.supporter};
`;

const Muted = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
  overflow-wrap: anywhere;
`;

const Bio = styled.p`
  margin: 6px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  overflow-wrap: anywhere;
  white-space: pre-line;
`;

const Facts = styled.ul`
  margin: 0;
  padding: 0 0 0 24px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-left: 1px solid ${({ theme }) => theme.color.glassBorder};
  @container (max-width: 1059.98px) {
    grid-column: 1 / -1;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px 20px;
    padding: 14px 0 0;
    border-left: 0;
    border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
`;

const Fact = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  > svg {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const STATUS_LABEL: Record<NonNullable<User['status']>, string> = {
  active: 'Active',
  suspended: 'Suspended',
  banned: 'Banned',
};

export function ProfileOverviewCard({ user }: { user: User }) {
  const followers = user.stats?.followers ?? user.followerCount ?? 0;
  const following = user.stats?.following ?? 0;
  const status = user.status ?? 'active';
  return (
    <DashCard labelledBy="acct-overview-title" span={4} spanMid={2} testId="profile-overview">
      <CardHead
        icon="user"
        title="Profile Overview"
        titleId="acct-overview-title"
        divided={false}
        aside={
          <LinkButton to="/settings/profile" $size="sm">
            <Icon name="edit" size={14} />
            Edit profile
          </LinkButton>
        }
      />
      <Body>
        <AvatarWrap>
          <Avatar name={user.name} avatarKey={user.avatarKey} size={104} />
          <CameraLink to="/settings/profile#photo" aria-label="Change profile photo">
            <Icon name="camera" size={16} />
          </CameraLink>
        </AvatarWrap>
        <Identity>
          <NameRow>
            <Name>{user.name}</Name>
            {user.verified && <Icon name="verify" size={20} title="Verified" />}
            {user.supporterBadge && (
              <Supporter>
                <Icon name="supporter-badge" size={18} title="Supporter" />
              </Supporter>
            )}
          </NameRow>
          <Muted>{user.handle}</Muted>
          {user.joined && (
            <Muted>
              <Icon name="calendar-clock" size={15} />
              Member since {user.joined}
            </Muted>
          )}
          {user.bio && <Bio>{user.bio}</Bio>}
        </Identity>
        <Facts aria-label="Account at a glance">
          <Fact>
            <Icon name={user.privateAccount ? 'lock' : 'globe'} size={16} />
            {user.privateAccount ? 'Private profile' : 'Public profile'}
          </Fact>
          <Fact>
            <Icon name="users" size={16} />
            {formatCount(followers)} {followers === 1 ? 'follower' : 'followers'}
          </Fact>
          <Fact>
            <Icon name="user-plus" size={16} />
            {formatCount(following)} following
          </Fact>
          <Fact>
            <Pill tone={status === 'active' ? 'success' : 'danger'} dot>
              {STATUS_LABEL[status]}
            </Pill>
          </Fact>
        </Facts>
      </Body>
    </DashCard>
  );
}
